import React, { useState, useEffect } from "react";
import axios from "axios";
import API_URL from "../../Config";
import { IoClose } from "react-icons/io5";
import { AiOutlineEye } from "react-icons/ai";
import "./purchaseIndents.css";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const EditPurchaseIndentPopup = ({ indentId, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState({
    iocl_approval: null,
    invoice: null,
    acknowledgment: null,
  });

  const [indentData, setIndentData] = useState({
    customer_id: "",
    indent_date: "",
    iocl_approval_file: null,
    invoice_quantity_kl: "",
    invoice_no_of_barrels: "",
    invoice_file: null,
    acknowledgment_file: null,
  });

  const tabs = [
    { id: "summary", label: "Summary", icon: "📋" },
    { id: "iocl", label: "IOCL Approval", icon: "✓" },
    { id: "invoice", label: "Invoice", icon: "🧾" },
    { id: "acknowledgment", label: "Acknowledgment", icon: "📄" },
  ];

  // -------------------- INITIAL LOAD --------------------
  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  // Separate useEffect for fetching indent details after products are loaded
  useEffect(() => {
    if (indentId && products.length > 0) {
      fetchIndentDetails(indentId);
    }
  }, [indentId, products]);

  // -------------------- FETCH CUSTOMERS --------------------
  const fetchCustomers = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/barrel/customers/minimal`,
        {
          headers: { Authorization: authKey },
        }
      );

      const formatted = response.data?.customers?.map((c) => ({
        id: c.id,
        customer_name: c.customer_name,
      }));
      setCustomers(formatted || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
      showSnackbar("Error loading customers", "error");
    }
  };

  // -------------------- FETCH PRODUCTS --------------------
  const fetchProducts = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      if (!authKey) {
        showSnackbar("Authorization key missing", "error");
        return;
      }

      const response = await axios.get(`${API_URL}/api/barrel/products`, {
        headers: { Authorization: authKey },
      });

      setProducts(response.data.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      showSnackbar("Failed to load products", "error");
    }
  };

  // -------------------- FETCH INDENT DETAILS --------------------
  const fetchIndentDetails = async (id) => {
    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(`${API_URL}/api/barrel/indent/${id}`, {
        headers: { Authorization: authKey },
      });

      const data = response.data;
      const indent = data?.indent;
      const items = data?.items || [];
      const attachments = data?.attachments || [];

      setIndentData((prev) => ({
        ...prev,
        customer_id: indent?.barrel_customer_id || "",
        indent_date: indent?.indent_date
          ? new Date(indent.indent_date).toISOString().split("T")[0]
          : "",
        invoice_quantity_kl:
          data?.invoice_details?.[0]?.invoice_quantity_kl || "",
        invoice_no_of_barrels: data?.invoice_details?.[0]?.barrel_count || "",
      }));

      // Match existing items with products from API to get the correct UUID
      const formattedProducts = items.map((item) => {
        // Find the matching product from the products list to get the UUID
        const matchingProduct = products.find(
          (p) => p.product_code === item.product_code
        );
        
        return {
          id: matchingProduct ? matchingProduct.id : item.product_code,
          product_code: item.product_code,
          name: item.product_name,
          quantity_kl: item.quantity,
          no_of_barrels: item.quantity_in_barrels || 0,
        };
      });

      setSelectedProducts(formattedProducts);

      // Map attachments to respective document types
      const docs = {
        iocl_approval:
          attachments.find((att) => att.document_type === "IOCL Approval") ||
          null,
        invoice:
          attachments.find((att) => att.document_type === "Invoice") || null,
        acknowledgment:
          attachments.find((att) => att.document_type === "Acknowledgement") ||
          null,
      };
      setUploadedDocuments(docs);
    } catch (error) {
      console.error("Error fetching indent:", error);
      showSnackbar("Error fetching indent details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------- INPUT HANDLERS --------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setIndentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setIndentData((prev) => ({ ...prev, [fieldName]: file }));
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleProductSelect = (e) => {
    const productId = e.target.value;
    if (!productId) return;

    // ✅ Check if product already selected by UUID id
    const alreadySelected = selectedProducts.find((p) => p.id === productId);
    if (alreadySelected) {
      showSnackbar("Product already selected!", "error");
      e.target.value = ""; // reset dropdown
      return;
    }

    // ✅ Add new product
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProducts([
        ...selectedProducts,
        {
          id: product.id,
          product_code: product.product_code,
          name: product.product_name,
          quantity_kl: "",
          no_of_barrels: 0,
        },
      ]);
    }

    e.target.value = "";
  };

  const handleProductQuantityChange = (productId, value) => {
    setSelectedProducts(
      selectedProducts.map((product) => {
        if (product.id === productId) {
          const quantityKl = parseFloat(value) || 0;
          const liters = quantityKl * 1000;
          const barrels = Math.ceil(liters / 200);
          return { ...product, quantity_kl: value, no_of_barrels: barrels };
        }
        return product;
      })
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter((p) => p.id !== productId));
  };

  const handleViewDocument = (filePath) => {
    if (filePath) {
      const fullPath = `${API_URL}${filePath}`;
      window.open(fullPath, "_blank");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  // -------------------- UPDATE HANDLER --------------------
  const handleUpdateClick = async () => {
    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");

      if (activeTab === "summary") {
        if (!indentData.customer_id)
          return showSnackbar("Customer is required", "error");
        if (!indentData.indent_date)
          return showSnackbar("Indent date is required", "error");
        if (selectedProducts.length === 0)
          return showSnackbar("At least one product is required", "error");

        const items = selectedProducts.map((p) => ({
          product_code: p.product_code,
          product_name: p.name,
          quantity: p.quantity_kl,
          uom: "KL",
        }));

        const payload = {
          barrel_customer_id: indentData.customer_id,
          indent_date: indentData.indent_date,
          items,
        };

        const response = await axios.put(
          `${API_URL}/api/barrel/indent/${indentId}`,
          payload,
          {
            headers: {
              Authorization: authKey,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          showSnackbar("Indent updated successfully!", "success");
          setTimeout(() => {
            onSuccess();
          }, 1200); // wait 1.2s before closing so Snackbar shows properly
        }
      }

      // Handle IOCL Approval file upload
      if (activeTab === "iocl" && indentData.iocl_approval_file) {
        const formData = new FormData();
        formData.append("file", indentData.iocl_approval_file);
        formData.append("document_type", "IOCL Approval");

        await axios.post(
          `${API_URL}/api/barrel/indent/${indentId}/upload`,
          formData,
          {
            headers: {
              Authorization: authKey,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        showSnackbar("IOCL Approval uploaded successfully!", "success");
        fetchIndentDetails(indentId);
      }

      // Handle Invoice file upload
      if (activeTab === "invoice" && indentData.invoice_file) {
        const formData = new FormData();
        formData.append("file", indentData.invoice_file);
        formData.append("document_type", "Invoice");
        formData.append("invoice_quantity_kl", indentData.invoice_quantity_kl);
        formData.append("barrel_count", indentData.invoice_no_of_barrels);

        await axios.post(
          `${API_URL}/api/barrel/indent/${indentId}/upload`,
          formData,
          {
            headers: {
              Authorization: authKey,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        showSnackbar("Invoice uploaded successfully!", "success");
        fetchIndentDetails(indentId);
      }

      // Handle Acknowledgment file upload
      if (activeTab === "acknowledgment" && indentData.acknowledgment_file) {
        const formData = new FormData();
        formData.append("file", indentData.acknowledgment_file);
        formData.append("document_type", "Acknowledgement");

        await axios.post(
          `${API_URL}/api/barrel/indent/${indentId}/upload`,
          formData,
          {
            headers: {
              Authorization: authKey,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        showSnackbar("Acknowledgment uploaded successfully!", "success");
        fetchIndentDetails(indentId);
      }
    } catch (error) {
      console.error("Error updating indent:", error);
      showSnackbar(error.response?.data?.message || "Error updating indent", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------- TABS RENDER --------------------
  const renderSummaryTab = () => (
    <div className="purchase_indent_tab-content">
      <div className="purchase_indent_form-group">
        <label>
          Indent Date<span className="text-danger">*</span>
        </label>
        <input
          type="date"
          name="indent_date"
          value={indentData.indent_date}
          onChange={handleInputChange}
          className="purchase_indent_input"
        />
      </div>

      <div className="purchase_indent_form-group">
        <label>
          Customer<span className="text-danger">*</span>
        </label>
        <select
          name="customer_id"
          value={indentData.customer_id}
          onChange={handleInputChange}
          className="purchase_indent_input"
        >
          <option value="">Select customer</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.customer_name}
            </option>
          ))}
        </select>
      </div>

      <div className="purchase_indent_products-section">
        <div className="purchase_indent_products-header">
          <h5>Products</h5>
          <select
            onChange={handleProductSelect}
            className="purchase_indent_product-dropdown"
          >
            <option value="">Select Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.product_name} ({p.product_code})
              </option>
            ))}
          </select>
        </div>

        <div className="purchase_indent_products-list">
          {selectedProducts.map((product) => (
            <div key={product.id} className="purchase_indent_product-card">
              <div className="purchase_indent_product-card-header">
                <h6>{product.name}</h6>
                <button
                  type="button"
                  onClick={() => handleRemoveProduct(product.id)}
                  className="purchase_indent_remove-btn"
                >
                  <IoClose />
                </button>
              </div>
              <div className="purchase_indent_product-card-body">
                <div className="purchase_indent_form-group">
                  <label>Quantity (KL)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={product.quantity_kl}
                    onChange={(e) =>
                      handleProductQuantityChange(product.id, e.target.value)
                    }
                    placeholder="Enter quantity in KL"
                    className="purchase_indent_input"
                  />
                </div>
                <div className="purchase_indent_form-group">
                  <label>No. of Barrels</label>
                  <input
                    type="text"
                    value={product.no_of_barrels}
                    readOnly
                    className="purchase_indent_input purchase_indent_readonly"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderIOCLTab = () => (
    <div className="purchase_indent_tab-content">
      <h5>IOCL Approval</h5>
      <div className="purchase_indent_form-group">
        <label>Upload IOCL Approval Document</label>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(e, "iocl_approval_file")}
            className="purchase_indent_input"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="purchase_indent_btn purchase_indent_btn-save"
            style={{ minWidth: "100px" }}
          >
            Upload
          </button>
        </div>
        <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
          Accepted formats: PDF, JPG, PNG
        </small>
      </div>

      {uploadedDocuments.iocl_approval && (
        <div
          style={{
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "8px",
            padding: "20px",
            marginTop: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "15px",
            }}
          >
            <span style={{ color: "#28a745", fontSize: "24px" }}>✓</span>
            <h6
              style={{
                margin: 0,
                color: "#28a745",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              IOCL Approval Uploaded Successfully
            </h6>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div>
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
              >
                Uploaded On
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {formatDate(uploadedDocuments.iocl_approval.uploaded_at)}
              </div>
            </div>
            <div>
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
              >
                Document
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  wordBreak: "break-all",
                }}
              >
                {uploadedDocuments.iocl_approval.file_name}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderInvoiceTab = () => (
    <div className="purchase_indent_tab-content">
      <h5>Invoice</h5>
      <div className="purchase_indent_form-group">
        <label>Invoice Quantity (KL)</label>
        <input
          type="number"
          step="0.01"
          name="invoice_quantity_kl"
          value={indentData.invoice_quantity_kl}
          onChange={handleInputChange}
          placeholder="Enter quantity in KL"
          className="purchase_indent_input"
        />
      </div>

      <div className="purchase_indent_form-group">
        <label>No. of Barrels</label>
        <input
          type="number"
          name="invoice_no_of_barrels"
          value={indentData.invoice_no_of_barrels}
          onChange={handleInputChange}
          placeholder="Enter number of barrels"
          className="purchase_indent_input"
        />
      </div>

      <div className="purchase_indent_form-group">
        <label>Upload Invoice Document</label>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(e, "invoice_file")}
            className="purchase_indent_input"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="purchase_indent_btn purchase_indent_btn-save"
            style={{ minWidth: "100px" }}
          >
            Upload
          </button>
        </div>
        <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
          Accepted formats: PDF, JPG, PNG
        </small>
      </div>

      {uploadedDocuments.invoice && (
        <div
          style={{
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "8px",
            padding: "20px",
            marginTop: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "15px",
            }}
          >
            <span style={{ color: "#28a745", fontSize: "24px" }}>✓</span>
            <h6
              style={{
                margin: 0,
                color: "#28a745",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Invoice Uploaded Successfully
            </h6>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div>
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
              >
                Uploaded On
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {formatDate(uploadedDocuments.invoice.uploaded_at)}
              </div>
            </div>
            <div>
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
              >
                Document
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  wordBreak: "break-all",
                }}
              >
                {uploadedDocuments.invoice.file_name}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAcknowledgmentTab = () => (
    <div className="purchase_indent_tab-content">
      <h5>Invoice Acknowledgment</h5>
      <div className="purchase_indent_form-group">
        <label>Upload Acknowledgment Document</label>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(e, "acknowledgment_file")}
            className="purchase_indent_input"
            style={{ flex: 1 }}
          />
        </div>
        <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
          Accepted formats: PDF, JPG, PNG
        </small>
      </div>

      {uploadedDocuments.acknowledgment && (
        <div
          style={{
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "8px",
            padding: "20px",
            marginTop: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "15px",
            }}
          >
            <span style={{ color: "#28a745", fontSize: "24px" }}>✓</span>
            <h6
              style={{
                margin: 0,
                color: "#28a745",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Acknowledgment Uploaded Successfully
            </h6>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div>
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
              >
                Uploaded On
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {formatDate(uploadedDocuments.acknowledgment.uploaded_at)}
              </div>
            </div>
            <div>
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
              >
                Document
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  wordBreak: "break-all",
                }}
              >
                {uploadedDocuments.acknowledgment.file_name}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // -------------------- MAIN RETURN --------------------
  return (
    <div className="purchase_indent_popup-overlay">
      <div className="purchase_indent_popup-modal">
        <div className="purchase_indent_popup-header">
          <h4>Edit Purchase Indent</h4>
          <span className="purchase_indent_close-btn" onClick={onClose}>
            <IoClose />
          </span>
        </div>

        {/* Tabs */}
        <div className="purchase_indent_tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`purchase_indent_tab ${
                activeTab === tab.id ? "active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="purchase_indent_popup-body">
          {activeTab === "summary" && renderSummaryTab()}
          {activeTab === "iocl" && renderIOCLTab()}
          {activeTab === "invoice" && renderInvoiceTab()}
          {activeTab === "acknowledgment" && renderAcknowledgmentTab()}
        </div>

        <div className="purchase_indent_popup-footer">
          <button
            className="purchase_indent_btn purchase_indent_btn-save"
            onClick={handleUpdateClick}
            disabled={isLoading}
          >
            {isLoading ? "Updating..." : "Update Indent"}
          </button>
          <button
            className="purchase_indent_btn purchase_indent_btn-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          style={{ marginTop: "35px", marginLeft: "20px" }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </div>
    </div>
  );
};

export default EditPurchaseIndentPopup;












// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import API_URL from "../../Config";
// import { IoClose } from "react-icons/io5";
// import { AiOutlineEye } from "react-icons/ai";
// import "./purchaseIndents.css";
// import Snackbar from "@mui/material/Snackbar";
// import MuiAlert from "@mui/material/Alert";

// const EditPurchaseIndentPopup = ({ indentId, onClose, onSuccess }) => {
//   const [activeTab, setActiveTab] = useState("summary");
//   const [isLoading, setIsLoading] = useState(false);
//   const [customers, setCustomers] = useState([]);
//   const [selectedProducts, setSelectedProducts] = useState([]);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [snackbarSeverity, setSnackbarSeverity] = useState("success");
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [uploadedDocuments, setUploadedDocuments] = useState({
//     iocl_approval: null,
//     invoice: null,
//     acknowledgment: null,
//   });

//   const [indentData, setIndentData] = useState({
//     customer_id: "",
//     indent_date: "",
//     iocl_approval_file: null,
//     invoice_quantity_kl: "",
//     invoice_no_of_barrels: "",
//     invoice_file: null,
//     acknowledgment_file: null,
//   });

//   const productOptions = [
//     { id: "50763", name: "50763" },
//     { id: "52563", name: "52563" },
//   ];

//   const tabs = [
//     { id: "summary", label: "Summary", icon: "📋" },
//     { id: "iocl", label: "IOCL Approval", icon: "✓" },
//     { id: "invoice", label: "Invoice", icon: "🧾" },
//     { id: "acknowledgment", label: "Acknowledgment", icon: "📄" },
//   ];

//   // -------------------- INITIAL LOAD --------------------
//   useEffect(() => {
//     fetchCustomers();
//     if (indentId) fetchIndentDetails(indentId);
//   }, [indentId]);

//   // -------------------- FETCH CUSTOMERS --------------------
//   const fetchCustomers = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(
//         `${API_URL}/api/barrel/customers/minimal`,
//         {
//           headers: { Authorization: authKey },
//         }
//       );

//       const formatted = response.data?.customers?.map((c) => ({
//         id: c.id,
//         customer_name: c.customer_name,
//       }));
//       setCustomers(formatted || []);
//     } catch (error) {
//       console.error("Error fetching customers:", error);
//       showSnackbar("Error loading customers");
//     }
//   };

//   // -------------------- FETCH INDENT DETAILS --------------------
//   const fetchIndentDetails = async (id) => {
//     try {
//       setIsLoading(true);
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(`${API_URL}/api/barrel/indent/${id}`, {
//         headers: { Authorization: authKey },
//       });

//       const data = response.data;
//       const indent = data?.indent;
//       const items = data?.items || [];
//       const attachments = data?.attachments || [];

//       setIndentData((prev) => ({
//         ...prev,
//         customer_id: indent?.barrel_customer_id || "",
//         indent_date: indent?.indent_date
//           ? new Date(indent.indent_date).toISOString().split("T")[0]
//           : "",
//         invoice_quantity_kl:
//           data?.invoice_details?.[0]?.invoice_quantity_kl || "",
//         invoice_no_of_barrels: data?.invoice_details?.[0]?.barrel_count || "",
//       }));

//       const formattedProducts = items.map((item) => ({
//         id: item.product_code,
//         name: item.product_name,
//         quantity_kl: item.quantity,
//         no_of_barrels: item.quantity_in_barrels || 0,
//       }));

//       setSelectedProducts(formattedProducts);

//       // Map attachments to respective document types
//       const docs = {
//         iocl_approval:
//           attachments.find((att) => att.document_type === "IOCL Approval") ||
//           null,
//         invoice:
//           attachments.find((att) => att.document_type === "Invoice") || null,
//         acknowledgment:
//           attachments.find((att) => att.document_type === "Acknowledgement") ||
//           null,
//       };
//       setUploadedDocuments(docs);
//     } catch (error) {
//       console.error("Error fetching indent:", error);
//       showSnackbar("Error fetching indent details");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // -------------------- INPUT HANDLERS --------------------
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setIndentData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleFileChange = (e, fieldName) => {
//     const file = e.target.files[0];
//     if (file) {
//       setIndentData((prev) => ({ ...prev, [fieldName]: file }));
//     }
//   };

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbarMessage(message);
//     setSnackbarOpen(true);
//     setSnackbarSeverity(severity);
//   };

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return;
//     setSnackbarOpen(false);
//   };

//   // const handleProductSelect = (e) => {
//   //   const productId = e.target.value;
//   //   if (productId && !selectedProducts.find((p) => p.id === productId)) {
//   //     const product = productOptions.find((p) => p.id === productId);
//   //     setSelectedProducts([
//   //       ...selectedProducts,
//   //       { id: product.id, name: product.name, quantity_kl: "", no_of_barrels: 0 },
//   //     ]);
//   //     e.target.value = "";
//   //   }
//   // };

//   const handleProductSelect = (e) => {
//     const productId = e.target.value;
//     if (!productId) return;

//     // ✅ Check if product already selected
//     const alreadySelected = selectedProducts.find((p) => p.id === productId);
//     if (alreadySelected) {
//       showSnackbar("Product already selected!", "error");
//       e.target.value = ""; // reset dropdown
//       return;
//     }

//     // ✅ Add new product
//     const product = productOptions.find((p) => p.id === productId);
//     if (product) {
//       setSelectedProducts([
//         ...selectedProducts,
//         {
//           id: product.id,
//           name: product.name,
//           quantity_kl: "",
//           no_of_barrels: 0,
//         },
//       ]);
//     }

//     e.target.value = "";
//   };

//   const handleProductQuantityChange = (productId, value) => {
//     setSelectedProducts(
//       selectedProducts.map((product) => {
//         if (product.id === productId) {
//           const quantityKl = parseFloat(value) || 0;
//           const liters = quantityKl * 1000;
//           const barrels = Math.ceil(liters / 200);
//           return { ...product, quantity_kl: value, no_of_barrels: barrels };
//         }
//         return product;
//       })
//     );
//   };

//   const handleRemoveProduct = (productId) => {
//     setSelectedProducts(selectedProducts.filter((p) => p.id !== productId));
//   };

//   const handleViewDocument = (filePath) => {
//     if (filePath) {
//       const fullPath = `${API_URL}${filePath}`;
//       window.open(fullPath, "_blank");
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = date.toLocaleString("en-US", { month: "short" });
//     const year = date.getFullYear();
//     const hours = String(date.getHours()).padStart(2, "0");
//     const minutes = String(date.getMinutes()).padStart(2, "0");
//     return `${day} ${month} ${year}, ${hours}:${minutes}`;
//   };

//   // -------------------- UPDATE HANDLER --------------------
//   const handleUpdateClick = async () => {
//     try {
//       setIsLoading(true);
//       const authKey = localStorage.getItem("authKey");

//       if (activeTab === "summary") {
//         if (!indentData.customer_id)
//           return showSnackbar("Customer is required");
//         if (!indentData.indent_date)
//           return showSnackbar("Indent date is required");
//         if (selectedProducts.length === 0)
//           return showSnackbar("At least one product is required");

//         const items = selectedProducts.map((p) => ({
//           product_code: p.id,
//           product_name: p.name,
//           quantity: p.quantity_kl,
//           uom: "KL",
//         }));

//         const payload = {
//           barrel_customer_id: indentData.customer_id,
//           indent_date: indentData.indent_date,
//           items,
//         };

//         const response = await axios.put(
//           `${API_URL}/api/barrel/indent/${indentId}`,
//           payload,
//           {
//             headers: {
//               Authorization: authKey,
//               "Content-Type": "application/json",
//             },
//           }
//         );

//         // if (response.status === 200) {
//         //   showSnackbar("Indent updated successfully!");
//         //   onSuccess();
//         // }
//         if (response.status === 200) {
//           showSnackbar("Indent updated successfully!");
//           setTimeout(() => {
//             onSuccess();
//           }, 1200); // wait 1.2s before closing so Snackbar shows properly
//         }
//       }

//       // Handle IOCL Approval file upload
//       if (activeTab === "iocl" && indentData.iocl_approval_file) {
//         const formData = new FormData();
//         formData.append("file", indentData.iocl_approval_file);
//         formData.append("document_type", "IOCL Approval");

//         await axios.post(
//           `${API_URL}/api/barrel/indent/${indentId}/upload`,
//           formData,
//           {
//             headers: {
//               Authorization: authKey,
//               "Content-Type": "multipart/form-data",
//             },
//           }
//         );
//         showSnackbar("IOCL Approval uploaded successfully!");
//         fetchIndentDetails(indentId);
//       }

//       // Handle Invoice file upload
//       if (activeTab === "invoice" && indentData.invoice_file) {
//         const formData = new FormData();
//         formData.append("file", indentData.invoice_file);
//         formData.append("document_type", "Invoice");
//         formData.append("invoice_quantity_kl", indentData.invoice_quantity_kl);
//         formData.append("barrel_count", indentData.invoice_no_of_barrels);

//         await axios.post(
//           `${API_URL}/api/barrel/indent/${indentId}/upload`,
//           formData,
//           {
//             headers: {
//               Authorization: authKey,
//               "Content-Type": "multipart/form-data",
//             },
//           }
//         );
//         showSnackbar("Invoice uploaded successfully!");
//         fetchIndentDetails(indentId);
//       }

//       // Handle Acknowledgment file upload
//       if (activeTab === "acknowledgment" && indentData.acknowledgment_file) {
//         const formData = new FormData();
//         formData.append("file", indentData.acknowledgment_file);
//         formData.append("document_type", "Acknowledgement");

//         await axios.post(
//           `${API_URL}/api/barrel/indent/${indentId}/upload`,
//           formData,
//           {
//             headers: {
//               Authorization: authKey,
//               "Content-Type": "multipart/form-data",
//             },
//           }
//         );
//         showSnackbar("Acknowledgment uploaded successfully!");
//         fetchIndentDetails(indentId);
//       }
//     } catch (error) {
//       console.error("Error updating indent:", error);
//       showSnackbar(error.response?.data?.message || "Error updating indent");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // -------------------- TABS RENDER --------------------
//   const renderSummaryTab = () => (
//     <div className="purchase_indent_tab-content">
//       <div className="purchase_indent_form-group">
//         <label>
//           Indent Date<span className="text-danger">*</span>
//         </label>
//         <input
//           type="date"
//           name="indent_date"
//           value={indentData.indent_date}
//           onChange={handleInputChange}
//           className="purchase_indent_input"
//         />
//       </div>

//       <div className="purchase_indent_form-group">
//         <label>
//           Customer<span className="text-danger">*</span>
//         </label>
//         <select
//           name="customer_id"
//           value={indentData.customer_id}
//           onChange={handleInputChange}
//           className="purchase_indent_input"
//         >
//           <option value="">Select customer</option>
//           {customers.map((c) => (
//             <option key={c.id} value={c.id}>
//               {c.customer_name}
//             </option>
//           ))}
//         </select>
//       </div>

//       <div className="purchase_indent_products-section">
//         <div className="purchase_indent_products-header">
//           <h5>Products</h5>
//           <select
//             onChange={handleProductSelect}
//             className="purchase_indent_product-dropdown"
//           >
//             <option value="">Select Product</option>
//             {productOptions.map((p) => (
//               <option key={p.id} value={p.id}>
//                 {p.name}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="purchase_indent_products-list">
//           {selectedProducts.map((product) => (
//             <div key={product.id} className="purchase_indent_product-card">
//               <div className="purchase_indent_product-card-header">
//                 <h6>{product.name}</h6>
//                 <button
//                   type="button"
//                   onClick={() => handleRemoveProduct(product.id)}
//                   className="purchase_indent_remove-btn"
//                 >
//                   <IoClose />
//                 </button>
//               </div>
//               <div className="purchase_indent_product-card-body">
//                 <div className="purchase_indent_form-group">
//                   <label>Quantity (KL)</label>
//                   <input
//                     type="number"
//                     step="0.01"
//                     value={product.quantity_kl}
//                     onChange={(e) =>
//                       handleProductQuantityChange(product.id, e.target.value)
//                     }
//                     placeholder="Enter quantity in KL"
//                     className="purchase_indent_input"
//                   />
//                 </div>
//                 <div className="purchase_indent_form-group">
//                   <label>No. of Barrels</label>
//                   <input
//                     type="text"
//                     value={product.no_of_barrels}
//                     readOnly
//                     className="purchase_indent_input purchase_indent_readonly"
//                   />
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );

//   const renderIOCLTab = () => (
//     <div className="purchase_indent_tab-content">
//       <h5>IOCL Approval</h5>
//       <div className="purchase_indent_form-group">
//         <label>Upload IOCL Approval Document</label>
//         <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
//           <input
//             type="file"
//             accept=".pdf,.jpg,.jpeg,.png"
//             onChange={(e) => handleFileChange(e, "iocl_approval_file")}
//             className="purchase_indent_input"
//             style={{ flex: 1 }}
//           />
//           <button
//             type="button"
//             className="purchase_indent_btn purchase_indent_btn-save"
//             style={{ minWidth: "100px" }}
//           >
//             Upload
//           </button>
//         </div>
//         <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
//           Accepted formats: PDF, JPG, PNG
//         </small>
//       </div>

//       {uploadedDocuments.iocl_approval && (
//         <div
//           style={{
//             backgroundColor: "#d4edda",
//             border: "1px solid #c3e6cb",
//             borderRadius: "8px",
//             padding: "20px",
//             marginTop: "20px",
//           }}
//         >
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: "10px",
//               marginBottom: "15px",
//             }}
//           >
//             <span style={{ color: "#28a745", fontSize: "24px" }}>✓</span>
//             <h6
//               style={{
//                 margin: 0,
//                 color: "#28a745",
//                 fontSize: "16px",
//                 fontWeight: "600",
//               }}
//             >
//               IOCL Approval Uploaded Successfully
//             </h6>
//           </div>
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "1fr 1fr",
//               gap: "15px",
//               marginBottom: "15px",
//             }}
//           >
//             <div>
//               <div
//                 style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
//               >
//                 Uploaded On
//               </div>
//               <div style={{ fontSize: "14px", fontWeight: "500" }}>
//                 {formatDate(uploadedDocuments.iocl_approval.uploaded_at)}
//               </div>
//             </div>
//             <div>
//               <div
//                 style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
//               >
//                 Document
//               </div>
//               <div
//                 style={{
//                   fontSize: "14px",
//                   fontWeight: "500",
//                   wordBreak: "break-all",
//                 }}
//               >
//                 {uploadedDocuments.iocl_approval.file_name}
//               </div>
//             </div>
//           </div>
//           {/* <button
//             type="button"
//             onClick={() =>
//               handleViewDocument(uploadedDocuments.iocl_approval.file_path)
//             }
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: "8px",
//               padding: "8px 16px",
//               backgroundColor: "transparent",
//               border: "1px solid #28a745",
//               borderRadius: "4px",
//               color: "#28a745",
//               fontSize: "14px",
//               fontWeight: "500",
//               cursor: "pointer",
//             }}
//           >
//             <AiOutlineEye size={18} />
//             View Document
//           </button> */}
//         </div>
//       )}
//     </div>
//   );

//   const renderInvoiceTab = () => (
//     <div className="purchase_indent_tab-content">
//       <h5>Invoice</h5>
//       <div className="purchase_indent_form-group">
//         <label>Invoice Quantity (KL)</label>
//         <input
//           type="number"
//           step="0.01"
//           name="invoice_quantity_kl"
//           value={indentData.invoice_quantity_kl}
//           onChange={handleInputChange}
//           placeholder="Enter quantity in KL"
//           className="purchase_indent_input"
//         />
//       </div>

//       <div className="purchase_indent_form-group">
//         <label>No. of Barrels</label>
//         <input
//           type="number"
//           name="invoice_no_of_barrels"
//           value={indentData.invoice_no_of_barrels}
//           onChange={handleInputChange}
//           placeholder="Enter number of barrels"
//           className="purchase_indent_input"
//         />
//       </div>

//       <div className="purchase_indent_form-group">
//         <label>Upload Invoice Document</label>
//         <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
//           <input
//             type="file"
//             accept=".pdf,.jpg,.jpeg,.png"
//             onChange={(e) => handleFileChange(e, "invoice_file")}
//             className="purchase_indent_input"
//             style={{ flex: 1 }}
//           />
//           <button
//             type="button"
//             className="purchase_indent_btn purchase_indent_btn-save"
//             style={{ minWidth: "100px" }}
//           >
//             Upload
//           </button>
//         </div>
//         <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
//           Accepted formats: PDF, JPG, PNG
//         </small>
//       </div>

//       {uploadedDocuments.invoice && (
//         <div
//           style={{
//             backgroundColor: "#d4edda",
//             border: "1px solid #c3e6cb",
//             borderRadius: "8px",
//             padding: "20px",
//             marginTop: "20px",
//           }}
//         >
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: "10px",
//               marginBottom: "15px",
//             }}
//           >
//             <span style={{ color: "#28a745", fontSize: "24px" }}>✓</span>
//             <h6
//               style={{
//                 margin: 0,
//                 color: "#28a745",
//                 fontSize: "16px",
//                 fontWeight: "600",
//               }}
//             >
//               Invoice Uploaded Successfully
//             </h6>
//           </div>
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "1fr 1fr",
//               gap: "15px",
//               marginBottom: "15px",
//             }}
//           >
//             <div>
//               <div
//                 style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
//               >
//                 Uploaded On
//               </div>
//               <div style={{ fontSize: "14px", fontWeight: "500" }}>
//                 {formatDate(uploadedDocuments.invoice.uploaded_at)}
//               </div>
//             </div>
//             <div>
//               <div
//                 style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
//               >
//                 Document
//               </div>
//               <div
//                 style={{
//                   fontSize: "14px",
//                   fontWeight: "500",
//                   wordBreak: "break-all",
//                 }}
//               >
//                 {uploadedDocuments.invoice.file_name}
//               </div>
//             </div>
//           </div>
//           {/* <button
//             type="button"
//             onClick={() =>
//               handleViewDocument(uploadedDocuments.invoice.file_path)
//             }
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: "8px",
//               padding: "8px 16px",
//               backgroundColor: "transparent",
//               border: "1px solid #28a745",
//               borderRadius: "4px",
//               color: "#28a745",
//               fontSize: "14px",
//               fontWeight: "500",
//               cursor: "pointer",
//             }}
//           >
//             <AiOutlineEye size={18} />
//             View Document
//           </button> */}
//         </div>
//       )}
//     </div>
//   );

//   const renderAcknowledgmentTab = () => (
//     <div className="purchase_indent_tab-content">
//       <h5>Invoice Acknowledgment</h5>
//       <div className="purchase_indent_form-group">
//         <label>Upload Acknowledgment Document</label>
//         <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
//           <input
//             type="file"
//             accept=".pdf,.jpg,.jpeg,.png"
//             onChange={(e) => handleFileChange(e, "acknowledgment_file")}
//             className="purchase_indent_input"
//             style={{ flex: 1 }}
//           />
//           {/* <button
//             type="button"
//             className="purchase_indent_btn purchase_indent_btn-save"
//             style={{ minWidth: "100px" }}
//           >
//             Upload
//           </button> */}
//         </div>
//         <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
//           Accepted formats: PDF, JPG, PNG
//         </small>
//       </div>

//       {uploadedDocuments.acknowledgment && (
//         <div
//           style={{
//             backgroundColor: "#d4edda",
//             border: "1px solid #c3e6cb",
//             borderRadius: "8px",
//             padding: "20px",
//             marginTop: "20px",
//           }}
//         >
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: "10px",
//               marginBottom: "15px",
//             }}
//           >
//             <span style={{ color: "#28a745", fontSize: "24px" }}>✓</span>
//             <h6
//               style={{
//                 margin: 0,
//                 color: "#28a745",
//                 fontSize: "16px",
//                 fontWeight: "600",
//               }}
//             >
//               Acknowledgment Uploaded Successfully
//             </h6>
//           </div>
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "1fr 1fr",
//               gap: "15px",
//               marginBottom: "15px",
//             }}
//           >
//             <div>
//               <div
//                 style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
//               >
//                 Uploaded On
//               </div>
//               <div style={{ fontSize: "14px", fontWeight: "500" }}>
//                 {formatDate(uploadedDocuments.acknowledgment.uploaded_at)}
//               </div>
//             </div>
//             <div>
//               <div
//                 style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}
//               >
//                 Document
//               </div>
//               <div
//                 style={{
//                   fontSize: "14px",
//                   fontWeight: "500",
//                   wordBreak: "break-all",
//                 }}
//               >
//                 {uploadedDocuments.acknowledgment.file_name}
//               </div>
//             </div>
//           </div>
//           {/* <button
//             type="button"
//             onClick={() => handleViewDocument(uploadedDocuments.acknowledgment.file_path)}
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: "8px",
//               padding: "8px 16px",
//               backgroundColor: "transparent",
//               border: "1px solid #28a745",
//               borderRadius: "4px",
//               color: "#28a745",
//               fontSize: "14px",
//               fontWeight: "500",
//               cursor: "pointer",
//             }}
//           >
//             <AiOutlineEye size={18} />
//             View Document
//           </button> */}
//         </div>
//       )}
//     </div>
//   );

//   // -------------------- MAIN RETURN --------------------
//   return (
//     <div className="purchase_indent_popup-overlay">
//       <div className="purchase_indent_popup-modal">
//         <div className="purchase_indent_popup-header">
//           <h4>Edit Purchase Indent</h4>
//           <span className="purchase_indent_close-btn" onClick={onClose}>
//             <IoClose />
//           </span>
//         </div>

//         {/* Tabs */}
//         <div className="purchase_indent_tabs">
//           {tabs.map((tab) => (
//             <button
//               key={tab.id}
//               className={`purchase_indent_tab ${
//                 activeTab === tab.id ? "active" : ""
//               }`}
//               onClick={() => setActiveTab(tab.id)}
//             >
//               {tab.icon} {tab.label}
//             </button>
//           ))}
//         </div>

//         <div className="purchase_indent_popup-body">
//           {activeTab === "summary" && renderSummaryTab()}
//           {activeTab === "iocl" && renderIOCLTab()}
//           {activeTab === "invoice" && renderInvoiceTab()}
//           {activeTab === "acknowledgment" && renderAcknowledgmentTab()}
//         </div>

//         <div className="purchase_indent_popup-footer">
//           <button
//             className="purchase_indent_btn purchase_indent_btn-save"
//             onClick={handleUpdateClick}
//             disabled={isLoading}
//           >
//             {isLoading ? "Updating..." : "Update Indent"}
//           </button>
//           <button
//             className="purchase_indent_btn purchase_indent_btn-cancel"
//             onClick={onClose}
//             disabled={isLoading}
//           >
//             Cancel
//           </button>
//         </div>

//         {/* Snackbar */}
//         <Snackbar
//           open={snackbarOpen}
//           autoHideDuration={6000}
//           onClose={handleSnackbarClose}
//           anchorOrigin={{ vertical: "top", horizontal: "right" }}
//           style={{ marginTop: "35px", marginLeft: "20px" }}
//         >
//           <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
//             {snackbarMessage}
//           </MuiAlert>
//         </Snackbar>
//       </div>
//     </div>
//   );
// };

// export default EditPurchaseIndentPopup;
